"use client";

import { usePathname } from "next/navigation";
import { Globe } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { writeRegionCookie } from "@/lib/regionContext";

interface Props {
  detectedLocale: "pt" | "en";
  detectedCountry: string | null;
  currentLocale: "pt" | "en";
}

const COUNTRY_NAMES: Record<string, { pt: string; en: string }> = {
  BR: { pt: "Brasil", en: "Brazil" },
  PT: { pt: "Portugal", en: "Portugal" },
  AO: { pt: "Angola", en: "Angola" },
  MZ: { pt: "Moçambique", en: "Mozambique" },
};

/**
 * EPIC-028 STORY-1601 — locale-mismatch prompt.
 *
 * Shown when the IP-detected locale differs from the cookie's
 * carried locale (and the cookie wasn't set by an explicit user
 * preference). Two actions: switch (writes new locale to the
 * cookie + reloads the page so SSR re-runs with the new locale),
 * or dismiss (writes the current locale back with `detected:false`
 * so we never re-prompt).
 */
export function RegionMismatchBanner({
  detectedLocale,
  detectedCountry,
  currentLocale,
}: Props) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [hidden, setHidden] = useState(false);
  // STORY-616 — embed routes are iframe-only; skip locale prompt.
  if (pathname?.startsWith("/embed")) return null;
  if (hidden) return null;

  // Banner copy renders in the *current* locale (via i18next's resolved
  // locale) so the existing user can read it.
  const countryName =
    detectedCountry && COUNTRY_NAMES[detectedCountry]
      ? COUNTRY_NAMES[detectedCountry][currentLocale]
      : detectedCountry ?? "";
  // The "switch to X?" question + dismiss action change based on the
  // direction of the proposed switch.
  const switchPromptKey =
    detectedLocale === "pt"
      ? "regionMismatchBanner.switchPrompt"
      : "regionMismatchBanner.switchPromptToEnglish";
  const dismissKey =
    currentLocale === "en"
      ? "regionMismatchBanner.keepEnglish"
      : "regionMismatchBanner.keepPortuguese";

  function handleSwitch() {
    writeRegionCookie({
      region: detectedLocale === "pt" ? "brazil" : "global",
      locale: detectedLocale,
      currency: detectedLocale === "pt" ? "BRL" : "USD",
      detected: true,
      countryCode: detectedCountry,
    });
    // Reload so SSR re-runs with the new locale + currency.
    window.location.reload();
  }

  function handleDismiss() {
    // Carry the current locale forward as an explicit choice so we
    // never re-prompt this user.
    writeRegionCookie({
      region: currentLocale === "pt" ? "brazil" : "global",
      locale: currentLocale,
      currency: currentLocale === "pt" ? "BRL" : "USD",
      detected: false,
      countryCode: detectedCountry,
    });
    setHidden(true);
  }

  return (
    <div
      role="status"
      className="w-full border-b text-[12px]"
      style={{
        borderColor: "var(--color-border)",
        background:
          "color-mix(in srgb, var(--color-status-info, #5cc8ff) 10%, var(--color-bg))",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-5 py-2">
        <span className="flex flex-1 min-w-0 items-center gap-2">
          <Globe size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>
            <span className="font-bold text-[var(--color-text)]">
              {t("regionMismatchBanner.youAreIn")} {countryName}.
            </span>{" "}
            <span className="text-[var(--color-text2)]">
              {t(switchPromptKey)}
            </span>
          </span>
        </span>
        <button
          type="button"
          onClick={handleSwitch}
          className="rounded-md px-3 py-1 font-bold text-white"
          style={{ background: "var(--color-brand)" }}
        >
          {t("regionMismatchBanner.switch")}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-md border border-[var(--color-border)] px-2 py-1 font-bold text-[var(--color-text3)] hover:text-[var(--color-text)]"
        >
          {t(dismissKey)}
        </button>
      </div>
    </div>
  );
}
